import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, updateProfile, sendEmailVerification, User as FirebaseUser } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { Mail, Lock, AlertCircle, ChevronDown, User, CheckCircle2, Circle, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocalizedRoutes } from '../../utils/routes';

// FirstPromoter referral tracking helper
declare global {
  interface Window {
    fpr?: (...args: any[]) => void;
  }
}

const trackFirstPromoterReferral = (payload: { email?: string; uid?: string }) => {
  try {
    if (typeof window !== 'undefined' && typeof window.fpr === 'function') {
      window.fpr('referral', payload);
    }
  } catch (err) {
    // Swallow errors to avoid impacting signup UX
    console.warn('FirstPromoter referral tracking failed:', err);
  }
};

// Initialize Firestore
const db = getFirestore();

const referralSources = [
  'Google',
  'YouTube',
  'Facebook',
  'Instagram',
  'LinkedIn',
  'Word of Mouth',
  'ProductHunt',
  'Email',
  'Invitation',
  'Referral',
  'Affiliate',
  'Other'
];

// Define the type for additional user data
interface AdditionalUserData {
  displayName?: string;
  referralSource?: string;
  [key: string]: any;
}

// Function to create a user document in Firestore
const createUserDocument = async (user: FirebaseUser, additionalData: AdditionalUserData = {}) => {
  if (!user) return;
  
  try {
    // Create a user document with the user's ID as the document ID
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || '',
      createdAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
      role: 'user', // Default role
      referralSource: additionalData.referralSource || '',
      // Add any other fields you need
      ...additionalData
    });
    
    console.log('User document created successfully in Firestore');
  } catch (error: unknown) {
    console.error('Error creating user document:', error);
  }
};

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const routes = useLocalizedRoutes();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // State for interactive password rules
  const [isLengthMet, setIsLengthMet] = useState(false);
  const [hasUppercaseMet, setHasUppercaseMet] = useState(false);
  const [hasNumberOrSymbolMet, setHasNumberOrSymbolMet] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const utmSource = queryParams.get('utm_source');
    const utmMedium = queryParams.get('utm_medium');
    let derivedReferralSource = '';

    if (utmMedium) {
      const mediumLower = utmMedium.toLowerCase();
      if (mediumLower === 'affiliate' && referralSources.includes('Affiliate')) {
        derivedReferralSource = 'Affiliate';
      } else if (mediumLower === 'referral' && referralSources.includes('Referral')) {
        derivedReferralSource = 'Referral';
      } else if (mediumLower === 'invitation' && referralSources.includes('Invitation')) {
        derivedReferralSource = 'Invitation';
      }
    }

    if (!derivedReferralSource && utmSource) {
      const sourceLower = utmSource.toLowerCase();
      // Attempt to map utm_source to a value in referralSources
      // This is a simple direct match, could be made more sophisticated
      const matchedSource = referralSources.find(rs => rs.toLowerCase() === sourceLower);
      if (matchedSource) {
        derivedReferralSource = matchedSource;
      } else if (sourceLower === 'google' && referralSources.includes('Google')) {
        derivedReferralSource = 'Google';
      } else if (sourceLower === 'youtube' && referralSources.includes('YouTube')) {
        derivedReferralSource = 'YouTube';
      } else if (sourceLower === 'facebook' && referralSources.includes('Facebook')) {
        derivedReferralSource = 'Facebook';
      } else if (sourceLower === 'instagram' && referralSources.includes('Instagram')) {
        derivedReferralSource = 'Instagram';
      } else if (sourceLower === 'linkedin' && referralSources.includes('LinkedIn')) {
        derivedReferralSource = 'LinkedIn';
      } else if (sourceLower === 'producthunt' && referralSources.includes('ProductHunt')) {
        derivedReferralSource = 'ProductHunt';
      } else if (sourceLower === 'email' && referralSources.includes('Email')) {
        derivedReferralSource = 'Email';
      }
      // Add more specific utm_source mappings if needed
    }

    if (derivedReferralSource) {
      setReferralSource(derivedReferralSource);
    }
  }, [location.search]);

  const validatePasswordRequirements = (currentPassword: string) => {
    // Rule 1: At least 8 characters
    setIsLengthMet(currentPassword.length >= 8);
    // Rule 2: Contains 1 uppercase character
    setHasUppercaseMet(/[A-Z]/.test(currentPassword));
    // Rule 3: Contains 1 number or symbol
    setHasNumberOrSymbolMet(/[0-9\W]/.test(currentPassword)); // \W matches non-alphanumeric
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    validatePasswordRequirements(newPassword);
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Password complexity validation based on new rules
    const isPasswordValid = isLengthMet && hasUppercaseMet && hasNumberOrSymbolMet;
    if (!isPasswordValid) {
      setError(t('auth.signup.errors.passwordRequirements'));
      return;
    }

    // Removed confirm password check per updated signup UX

    if (!referralSource) {
      setError(t('auth.signup.errors.referralRequired'));
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      setError(t('auth.signup.errors.nameRequired'));
      return;
    }

    setIsLoading(true);

    try {
      // 6. Firebase Auth: Create User
      console.log('Attempting Firebase user creation...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user; 
      console.log('Firebase user created successfully:', user.uid);
      
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      
      // 7. Firebase Auth: Update Profile
      console.log('Attempting Firebase profile update...');
      await updateProfile(user, {
        displayName: fullName
      });
      console.log('Firebase profile updated successfully.');

      // 8. Firebase Auth: Get Token
      console.log('Attempting to get ID token...');
      const idToken = await user.getIdToken();
      console.log('ID token received.');
            
      // Get UTM parameters from URL
      const queryParams = new URLSearchParams(location.search);
      const utmSource = queryParams.get('utm_source');
      const utmMedium = queryParams.get('utm_medium');
      const utmCampaign = queryParams.get('utm_campaign');
      const utmTerm = queryParams.get('utm_term');
      const utmContent = queryParams.get('utm_content');

      // Capture affiliate cookie (fp_ref) and persist locally for later use in checkout metadata
      try {
        const fpRefMatch = typeof document !== 'undefined' ? document.cookie.match(/(?:^|; )fp_ref=([^;]+)/) : null;
        const fpRef = fpRefMatch ? decodeURIComponent(fpRefMatch[1]) : undefined;
        if (fpRef) {
          localStorage.setItem('fp_ref', fpRef);
        }
      } catch {}

      // Call the backend worker to handle user document creation/check
      try {
        // Use the standard API URL pattern for the project
        const API_URL = import.meta.env.VITE_API_URL || 'https://api.socialness.ai';
        const backendUrl = `${API_URL}/firebase`; // Base URL for firebase endpoints

        if (!backendUrl) {
          throw new Error("Backend URL could not be constructed.");
        }

        console.log('Calling backend /check-user-signup...');
        const response = await fetch(`${backendUrl}/check-user-signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            idToken: idToken,
            profileData: {
              email: email,
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              password: password,
              photoURL: user.photoURL || '',
              returnSecureToken: true,
              referralSource: referralSource,
              utm_source: utmSource,
              utm_medium: utmMedium,
              utm_campaign: utmCampaign,
              utm_term: utmTerm,
              utm_content: utmContent,
              // Persist affiliate reference on user profile
              fp_ref: (typeof localStorage !== 'undefined' && localStorage.getItem('fp_ref')) || (typeof document !== 'undefined' && (document.cookie.match(/(?:^|; )fp_ref=([^;]+)/)?.[1] ? decodeURIComponent(document.cookie.match(/(?:^|; )fp_ref=([^;]+)/)![1]) : '')) || ''
            }
          }), 
        });

        const responseData = await response.json();

        if (!response.ok) {
          console.error('Backend signup check failed:', responseData);
          setError(responseData.error || t('auth.signup.errors.backendError', 'Signup process failed. Please try again.'));
          return;
        }

        console.log('Backend signup check successful:', responseData);

        // If backend is successful, continue with frontend steps
        trackFirstPromoterReferral({ email });
        await sendEmailVerification(user); 
        alert(t('auth.verification.checkInbox'));
        navigate('/'); 

      } catch (error) {
         console.error('Error calling backend signup check:', error);
         setError(t('auth.signup.errors.networkError', 'An error occurred during signup. Please check your connection and try again.'));
         return; 
      }

    } catch (err: any) {
      console.error('Error during Firebase operations:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError(t('auth.signup.errors.emailAlreadyInUse', 'A Socialness account already exists with this email. Please log in or reset your password.'));
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError('');
    
    if (!referralSource) {
      setError(t('auth.signup.errors.referralRequired'));
      return;
    }

    setIsLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Get the ID token AFTER Google sign-in
      const idToken = await user.getIdToken();
            
      // Get UTM parameters from URL
      const queryParams = new URLSearchParams(location.search);
      const utmSource = queryParams.get('utm_source');
      const utmMedium = queryParams.get('utm_medium');
      const utmCampaign = queryParams.get('utm_campaign');
      const utmTerm = queryParams.get('utm_term');
      const utmContent = queryParams.get('utm_content');

      // Extract first and last name from display name if available
      let userFirstName = '';
      let userLastName = '';
      if (user.displayName) {
        const nameParts = user.displayName.split(' ');
        userFirstName = nameParts[0] || '';
        userLastName = nameParts.slice(1).join(' ') || '';
      }
      
      // Capture affiliate cookie (fp_ref) and persist locally
      try {
        const fpRefMatch = typeof document !== 'undefined' ? document.cookie.match(/(?:^|; )fp_ref=([^;]+)/) : null;
        const fpRef = fpRefMatch ? decodeURIComponent(fpRefMatch[1]) : undefined;
        if (fpRef) {
          localStorage.setItem('fp_ref', fpRef);
        }
      } catch {}

      // Call the backend worker to handle user document creation/check
      try {
        // Use the standard API URL pattern for the project
        const API_URL = import.meta.env.VITE_API_URL || 'https://api.socialness.ai';
        const backendUrl = `${API_URL}/firebase`; // Base URL for firebase endpoints

        if (!backendUrl) {
          throw new Error("Backend URL could not be constructed.");
        }

        console.log('Calling backend /check-user-signup via Google...');
        const response = await fetch(`${backendUrl}/check-user-signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            idToken: idToken,
            profileData: {
              email: user.email || '',
              firstName: userFirstName,
              lastName: userLastName,
              photoURL: user.photoURL || '',
              returnSecureToken: true,
              referralSource: referralSource,
              utm_source: utmSource,
              utm_medium: utmMedium,
              utm_campaign: utmCampaign,
              utm_term: utmTerm,
              utm_content: utmContent,
              fp_ref: (typeof localStorage !== 'undefined' && localStorage.getItem('fp_ref')) || (typeof document !== 'undefined' && (document.cookie.match(/(?:^|; )fp_ref=([^;]+)/)?.[1] ? decodeURIComponent(document.cookie.match(/(?:^|; )fp_ref=([^;]+)/)![1]) : '')) || ''
            }
          }),
        });

        const responseData = await response.json();

        if (!response.ok) {
          console.error('Backend Google signup check failed:', responseData);
          setError(responseData.error || t('auth.signup.errors.backendError', 'Signup process failed. Please try again.'));
          return;
        }

        console.log('Backend Google signup check successful:', responseData);
        
        // If backend is successful, navigate
        trackFirstPromoterReferral({ email: user.email || undefined });
        navigate('/');

      } catch (error) {
         console.error('Error calling backend signup check via Google:', error);
         setError(t('auth.signup.errors.networkError', 'An error occurred during signup. Please check your connection and try again.'));
         return;
      }

    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div>
            <img
              src="/assets/Socialness-Logo/Socialness-Icon-1024x1024.png"
              alt="Socialness"
              className="mx-auto h-16 w-16"
            />
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {t('auth.signup.title')}
            </h2>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form className="mt-8 space-y-6" onSubmit={handleEmailSignUp}>
            <div className="rounded-md shadow-sm space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="sr-only">
                    {t('auth.signup.firstNameLabel') || 'First Name'}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      autoComplete="given-name"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="appearance-none relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                      placeholder={t('auth.signup.firstNamePlaceholder') || 'First Name'}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="lastName" className="sr-only">
                    {t('auth.signup.lastNameLabel') || 'Last Name'}
                  </label>
                  <div className="relative">
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                      placeholder={t('auth.signup.lastNamePlaceholder') || 'Last Name'}
                    />
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="email" className="sr-only">
                  {t('auth.signup.emailLabel')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder={t('auth.signup.emailPlaceholder')}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  {t('auth.signup.passwordLabel')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={handlePasswordChange}
                    className="appearance-none relative block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder={t('auth.signup.passwordPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                <div className="mt-2 text-xs text-gray-600 space-y-1">
                  <ul className="space-y-1">
                    <li className={`flex items-center ${isLengthMet ? 'text-green-600' : 'text-gray-500'}`}>
                      {isLengthMet ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Circle className="w-4 h-4 mr-2 text-gray-400" />}
                      {t('auth.signup.passwordRules.minLength')}
                    </li>
                    <li className={`flex items-center ${hasUppercaseMet ? 'text-green-600' : 'text-gray-500'}`}>
                      {hasUppercaseMet ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Circle className="w-4 h-4 mr-2 text-gray-400" />}
                      {t('auth.signup.passwordRules.uppercaseLetter')}
                    </li>
                    <li className={`flex items-center ${hasNumberOrSymbolMet ? 'text-green-600' : 'text-gray-500'}`}>
                      {hasNumberOrSymbolMet ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Circle className="w-4 h-4 mr-2 text-gray-400" />}
                      {t('auth.signup.passwordRules.numberOrSymbol')}
                    </li>
                  </ul>
                </div>
              </div>
              
              <div>
                <label htmlFor="referral-source" className="sr-only">
                  {t('auth.signup.referralLabel')}
                </label>
                <div className="relative">
                  <select
                    id="referral-source"
                    name="referral-source"
                    required
                    value={referralSource}
                    onChange={(e) => setReferralSource(e.target.value)}
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  >
                    <option value="">{t('auth.signup.referralPlaceholder')}</option>
                    {referralSources.map((source) => (
                      <option key={source} value={source}>{source}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>

            <div className="text-sm">
              <Link to={`${routes.auth.login}${location.search}`} className="font-medium text-primary-600 hover:text-primary-500">
                {t('auth.signup.hasAccount')}
              </Link>
            </div>

            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                {t('auth.signup.termsText')}{' '}
                <a 
                  href="https://www.socialness.ai/legal/terms-of-service" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-primary-600 hover:text-primary-500"
                >
                  {t('auth.signup.termsLink')}
                </a>{' '}
                {t('auth.signup.andThe')}{' '}
                <a 
                  href="https://www.socialness.ai/legal/privacy-policy" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-primary-600 hover:text-primary-500"
                >
                  {t('auth.signup.privacyLink')}
                </a>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? t('auth.signup.creating') : t('auth.signup.createButton')}
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-50 text-gray-500">{t('auth.signup.orContinueWith')}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleGoogleSignUp}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                {t('auth.signup.google')}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative flex-1">
        <img
          src="https://media.socialness.ai/Landing+Page/Marketing+agency+using+Socialness.webp"
          alt="Marketing agency using Socialness.ai"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
      </div>
    </div>
  );
};

export default SignUp;